import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import api, { API_BASE_URL } from "../../utils/api";

// ✅ U.S. States with FIPS Codes for Census API
const usStates = [
  { name: "Alabama", code: "01" }, { name: "Alaska", code: "02" }, { name: "Arizona", code: "04" },
  { name: "Arkansas", code: "05" }, { name: "California", code: "06" }, { name: "Colorado", code: "08" },
  { name: "Connecticut", code: "09" }, { name: "Delaware", code: "10" }, { name: "Florida", code: "12" },
  { name: "Georgia", code: "13" }, { name: "Hawaii", code: "15" }, { name: "Idaho", code: "16" },
  { name: "Illinois", code: "17" }, { name: "Indiana", code: "18" }, { name: "Iowa", code: "19" },
  { name: "Kansas", code: "20" }, { name: "Kentucky", code: "21" }, { name: "Louisiana", code: "22" },
  { name: "Maine", code: "23" }, { name: "Maryland", code: "24" }, { name: "Massachusetts", code: "25" },
  { name: "Michigan", code: "26" }, { name: "Minnesota", code: "27" }, { name: "Mississippi", code: "28" },
  { name: "Missouri", code: "29" }, { name: "Montana", code: "30" }, { name: "Nebraska", code: "31" },
  { name: "Nevada", code: "32" }, { name: "New Hampshire", code: "33" }, { name: "New Jersey", code: "34" },
  { name: "New Mexico", code: "35" }, { name: "New York", code: "36" }, { name: "North Carolina", code: "37" },
  { name: "North Dakota", code: "38" }, { name: "Ohio", code: "39" }, { name: "Oklahoma", code: "40" },
  { name: "Oregon", code: "41" }, { name: "Pennsylvania", code: "42" }, { name: "Rhode Island", code: "44" },
  { name: "South Carolina", code: "45" }, { name: "South Dakota", code: "46" }, { name: "Tennessee", code: "47" },
  { name: "Texas", code: "48" }, { name: "Utah", code: "49" }, { name: "Vermont", code: "50" },
  { name: "Virginia", code: "51" }, { name: "Washington", code: "53" }, { name: "West Virginia", code: "54" },
  { name: "Wisconsin", code: "55" }, { name: "Wyoming", code: "56" }
];

const Company = () => {
  const [companies, setCompanies] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");

  const [formData, setFormData] = useState({
    companyID: "",
    name: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    email: "",
    website: "",
    createdAt: "",
    updatedAt: "",
  });

  // ✅ Fetch Companies
  useEffect(() => {
    api.get(`/companies`)
      .then((res) => setCompanies(res.data))
      .catch((err) => console.error("❌ Error loading companies:", err));
  }, []);
  

  // ✅ Fetch Cities when State Changes
  const fetchCities = async (stateFips) => {
    try {
      const response = await axios.get(
        `https://api.census.gov/data/2019/pep/population?get=NAME&for=place:*&in=state:${stateFips}`
      );
      if (response.data.length > 1) {
        setCities(response.data.slice(1).map((item) => item[0]).sort());
      }
    } catch (error) {
      console.error("❌ Error fetching cities:", error);
    }
  };

  // ✅ Load Selected Company Details & Fetch Cities
  const handleCompanySelect = async (companyId) => {
    setSelectedCompany(companyId);

    if (!companyId) {
      setFormData({ companyID: "", name: "", address: "", city: "", state: "", zipCode: "", phoneNumber: "", email: "", website: "", createdAt: "", updatedAt: "" });
      setCities([]);
      return;
    }

    try {
      const response = await api.get(`/companies/${companyId}`);
      const company = response.data;

      setFormData({
        companyID: company.CompanyID || "",
        name: company.Name || "",
        address: company.Address || "",
        city: company.City || "",
        state: company.State || "",
        zipCode: company.ZipCode || "",
        phoneNumber: company.PhoneNumber || "",
        email: company.Email || "",
        website: company.Website || "",
        createdAt: company.CreatedAt || "",
        updatedAt: company.UpdatedAt || "",
      });

      fetchCities(company.State);
    } catch (error) {
      console.error("❌ Error fetching company details:", error);
    }
  };

  // ✅ Handle Input Changes
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // ✅ Save (Create or Update)
  const handleSave = async () => {
    if (!formData.name || formData.name.trim() === "") {
      setSnackbar({ open: true, message: "Company name is required!", severity: "error" });
      return;
    }
  
    const selectedState = usStates.find((state) => state.code === formData.state);
    const stateName = selectedState ? selectedState.name : null;
  
    if (!stateName || typeof stateName !== "string") {
      setSnackbar({ open: true, message: "Invalid state selection!", severity: "error" });
      return;
    }
  
    const payload = {
      Name: formData.name.trim(),
      Address: formData.address?.trim() || null,
      City: formData.city?.trim() || null,
      State: stateName,
      ZipCode: formData.zipCode?.trim() || null,
      PhoneNumber: formData.phoneNumber?.trim() || null,
      Email: formData.email?.trim() || null,
      Website: formData.website?.trim() || null,
    };
  
    try {
      if (selectedCompany) {
        await api.put(`/companies/${parseInt(selectedCompany)}`, payload); // ✅ UPDATE
      } else {
        await api.post(`/companies`, payload); // ✅ CREATE
      }
  
      setSnackbar({
        open: true,
        message: selectedCompany
          ? "Company updated successfully! ✅"
          : "Company created successfully! 🎉",
        severity: "success",
      });
  
      // ✅ Reload companies after save
      const updatedCompanies = await api.get(`/companies`);
      setCompanies(updatedCompanies.data);
  
    } catch (error) {
      console.error("❌ Error saving company:", error);
      console.log("❌ API Response Data:", error.response?.data);
      setSnackbar({ open: true, message: "Failed to save company!", severity: "error" });
    }
  };
  


  
  // ✅ Delete with Confirmation
  const handleDelete = async () => {
    await api.delete(`/companies/${selectedCompany}`);
    setSnackbar({ open: true, message: "Company deleted!", severity: "success" });

    setSelectedCompany("");
    setFormData({ companyID: "", name: "", address: "", city: "", state: "", zipCode: "", phoneNumber: "", email: "", website: "", createdAt: "", updatedAt: "" });

    const updatedCompanies = await axios.get(`${API_BASE_URL}/companies`);
    setCompanies(updatedCompanies.data);
  };

  

  return (
    <Paper sx={{ maxWidth: 600, p: 4, mx: "auto", mt: 4 }}>
      <Typography variant="h5">Manage Companies</Typography>

      <FormControl fullWidth>
        <InputLabel>Company</InputLabel>
        <Select value={selectedCompany} onChange={(e) => handleCompanySelect(e.target.value)}>
          <MenuItem value="">Add New</MenuItem>
          {companies.map((c) => <MenuItem key={c.CompanyID} value={c.CompanyID}>{c.Name}</MenuItem>)}
        </Select>
      </FormControl>

      <TextField label="Company Name" name="name" fullWidth value={formData.name} onChange={handleChange} margin="normal" />
      <TextField label="Address" name="address" fullWidth value={formData.address} onChange={handleChange} margin="normal" />

      <FormControl fullWidth margin="normal">
        <InputLabel>State</InputLabel>
        <Select name="state" value={formData.state} onChange={(e) => { handleChange(e); fetchCities(e.target.value); }}>
          {usStates.map((state) => <MenuItem key={state.code} value={state.code}>{state.name}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel>City</InputLabel>
        <Select name="city" value={formData.city} onChange={handleChange}>
          {cities.map((city) => <MenuItem key={city} value={city}>{city}</MenuItem>)}
        </Select>
      </FormControl>
      
      <TextField label="ZipCode" name="zipCode" fullWidth value={formData.zipCode} onChange={handleChange} margin="normal" />
      <TextField label="Phone Number" name="phoneNumber" fullWidth value={formData.phoneNumber} onChange={handleChange} margin="normal" />


      <TextField label="Email" name="email" fullWidth value={formData.email} onChange={handleChange} margin="normal" />
      <TextField label="Website" name="website" fullWidth value={formData.website} onChange={handleChange} margin="normal" />


      <Button variant="contained" onClick={handleSave}>Save</Button>
      {selectedCompany && <Button color="error" onClick={handleDelete}>Delete</Button>}
      <Snackbar
  open={snackbar.open}
  autoHideDuration={3000}
  onClose={() => setSnackbar({ ...snackbar, open: false })}
>
  <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
    {snackbar.message}
  </Alert>
</Snackbar>
    </Paper>
    
  );
};

export default Company;
